import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Heart, Award, ExternalLink, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n, cur = 'eur') => new Intl.NumberFormat(undefined, { style: 'currency', currency: (cur || 'eur').toUpperCase() }).format(Number(n || 0));

const DonateSuccess = () => {
    const [sp] = useSearchParams();
    const sessionId = sp.get('session_id');
    const [state, setState] = useState({ status: 'pending' });
    const [portalLoading, setPortalLoading] = useState(false);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) return;
        let stop = false;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/public/donations/status/${sessionId}`);
                if (stop) return;
                setState(data);
                if (data.status !== 'completed' && attempts.current < 15) {
                    attempts.current += 1;
                    setTimeout(poll, 2000);
                }
            } catch (e) {
                if (attempts.current < 3) {
                    attempts.current += 1;
                    setTimeout(poll, 2000);
                } else {
                    setState({ status: 'error' });
                }
            }
        };
        poll();
        return () => { stop = true; };
    }, [sessionId]);

    const openPortal = async () => {
        setPortalLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/public/donations/portal/${sessionId}`, { return_url: window.location.origin + '/donate' });
            window.location.href = data.portal_url;
        } catch {
            setPortalLoading(false);
            alert('Could not open the subscription portal. Please email info@nhq.ie for help.');
        }
    };

    if (!sessionId) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
                <div className="text-center">
                    <p className="text-lg text-[#4B5563]">No donation session found.</p>
                    <Link to="/donate" className="mt-3 inline-block text-[#064E3B] underline">Go back to donate page</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#FAF9F6] to-[#F3F4F6] flex items-center justify-center px-4 py-10" data-testid="donate-success-page">
            <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl p-10 text-center border border-[#E5E7EB]">
                {state.status === 'pending' && (
                    <>
                        <Loader2 className="h-12 w-12 text-[#064E3B] animate-spin mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-[#064E3B] font-['Outfit']">Confirming your donation…</h1>
                        <p className="mt-2 text-sm text-[#4B5563]">This usually takes a few seconds.</p>
                    </>
                )}
                {state.status === 'completed' && (
                    <>
                        <div className="w-20 h-20 rounded-full bg-[#DCFCE7] mx-auto mb-5 flex items-center justify-center">
                            <CheckCircle className="h-10 w-10 text-[#16A34A]" />
                        </div>
                        <h1 className="text-3xl font-bold text-[#064E3B] font-['Outfit']" data-testid="success-title">Thank you, {state.donor_name || 'friend'}!</h1>
                        <p className="mt-3 text-lg text-[#1F2937]">Your donation of <span className="font-bold text-[#064E3B]" data-testid="success-amount">{fmt(state.amount, state.currency)}</span>{state.mode && state.mode !== 'one_time' ? ` (${state.mode === 'weekly' ? 'weekly' : 'monthly'})` : ''} has been received.</p>
                        <p className="mt-2 text-sm text-[#4B5563]">May Allah accept and multiply your reward.</p>

                        {state.certificate && (
                            <p className="mt-6 text-sm text-[#B45309] flex items-center justify-center gap-2 bg-[#FEF3C7] p-3 rounded-lg" data-testid="cert-notice">
                                <Award className="h-4 w-4" />Your PDF certificate is on its way to your inbox.
                            </p>
                        )}

                        {state.mode && state.mode !== 'one_time' && state.customer_id && (
                            <button onClick={openPortal} disabled={portalLoading}
                                className="mt-6 w-full py-3 bg-white hover:bg-[#F9FAFB] text-[#064E3B] border-2 border-[#064E3B] rounded-lg font-semibold flex items-center justify-center gap-2"
                                data-testid="manage-subscription-btn">
                                {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                                Manage recurring donation
                            </button>
                        )}

                        <Link to="/donate" className="mt-6 inline-block text-sm text-[#4B5563] hover:text-[#064E3B]">← Back to donation page</Link>
                    </>
                )}
                {state.status === 'error' && (
                    <>
                        <p className="text-lg text-[#DC2626]">We couldn't confirm your donation yet.</p>
                        <p className="mt-2 text-sm text-[#4B5563]">If your card was charged, please email <a className="underline" href="mailto:info@nhq.ie">info@nhq.ie</a> with reference <code className="bg-[#F3F4F6] px-2 py-0.5 rounded">{sessionId.slice(0, 12)}</code></p>
                    </>
                )}
            </div>
        </div>
    );
};

export default DonateSuccess;
